import {SocketLobbySend} from '../../socket/SocketLobbySend'
import {NetworkEnums} from '../../../../../shared/GameEnums'
import {UILobbyTableRowController} from './UILobbyTableRowController'
import {SocketDataTypes} from '../../../../../shared/SocketDataTypes'
import {notificationController, socket} from '../../EntryShared'
import {LobbyImage, TimeoutId} from '@colonist/types'
import RoomUpdateData = NetworkEnums.RoomUpdateData
import {GameSorter} from '../../../../../shared/GameSorter'
import {createDivIn} from '../../ui_utils/DomUtils'

interface Room {
    room: NetworkEnums.RoomUpdateData
    lobbyRow: UILobbyTableRowController
}

export class UILobbyRoomListController {
    private readonly rooms: Map<string, Room> = new Map()
    private readonly tableBody: HTMLElement
    private readonly header: HTMLElement
    private readonly onRoomClick: (roomId: string) => void
    private clearRoomsInterval: TimeoutId | undefined
    private removeRooms: boolean = false
    private readonly unregisterSessionEstablishedObserver: () => void

    constructor(tableBody: HTMLElement, header: HTMLElement, onRoomClick: (roomId: string) => void) {
        this.tableBody = tableBody
        this.header = header
        this.onRoomClick = onRoomClick

        this.unregisterSessionEstablishedObserver = socket.controller.registerSessionEstablishedObserver(() => {
            const isListDisplayed = this.clearRoomsInterval !== undefined
            if(isListDisplayed) {
                this.clear()
                SocketLobbySend.watchRoomList()
            }
        })
    }

    activate() {
        this.clear()
        SocketLobbySend.watchRoomList()

        this.clearRoomsInterval = setInterval(() => this.clearRooms(), 2 * 1000) // interval needs to match room_update_progress_animation
    }

    destroy() {
        clearInterval(this.clearRoomsInterval)
        SocketLobbySend.stopWatchingRoomList()
        this.clear()
        this.unregisterSessionEstablishedObserver()
    }

    update(data: NetworkEnums.RoomListUpdateData, usersKarma: number | undefined, unavailableRooms: Set<string>) {
        if(data.full) this.clear()

        const rooms = this.getInitialRoomsSorted(data.rooms, usersKarma)

        for(const room of rooms) {
            if(!this.rooms.has(room.id)) {
                this.rooms.set(room.id, {
                    room: room,
                    lobbyRow: new UILobbyTableRowController(this.tableBody, convertRoomUpdateDataToLobbyRowData(room), (roomId) => this.clickedRoom(roomId)),
                })
            }
            const existingRoom = this.rooms.get(room.id) as Room
            existingRoom.room = room
            existingRoom.lobbyRow.update(convertRoomUpdateDataToLobbyRowData(room))
            const isRoomUnavailable = !existingRoom.room.visible || unavailableRooms.has(room.id)
            existingRoom.lobbyRow.setRoomAvailable(usersKarma, isRoomUnavailable)
        }
    }

    setRoomUnavailable(roomId: string) {
        const existingRoom = this.rooms.get(roomId)
        existingRoom?.lobbyRow.setRoomAvailable(undefined, true)
    }

    private clearRooms() {
        if(this.removeRooms) {
            const removedRooms = Array.from(this.rooms.values()).filter((room) => !room.room.visible)
            for(const room of removedRooms) {
                room.lobbyRow.remove()
                this.rooms.delete(room.room.id)
            }
        }

        for(const progressElements of this.header.getElementsByClassName('room_update_progress')) {
            this.header.removeChild(progressElements)
        }

        this.removeRooms = this.rooms.size > 0
        createDivIn(this.header, `room_update_progress ${this.removeRooms ? 'room_update_progress_animation' : ''}`)
    }

    private getInitialRoomsSorted(rooms: RoomUpdateData[], usersKarma: number | undefined): RoomUpdateData[] {
        if(this.rooms.size) {
            return rooms
        }

        const roomSorter = new GameSorter()
        return roomSorter.sort(rooms.map(convertRoomUpdateDataToLobbyRowData), usersKarma) as any as RoomUpdateData[]
    }

    private clickedRoom(roomId: string) {
        if(this.rooms.get(roomId)?.room.visible != true) {
            notificationController.showNotification(LobbyImage.Lightning, {key: 'strings:homePage.lobbyPage.popups.roomNoLongerExists'})
            return
        }

        this.onRoomClick(roomId)
    }

    private clear() {
        this.rooms.clear()
        this.tableBody.replaceChildren()
    }
}

function convertRoomUpdateDataToLobbyRowData(room: RoomUpdateData): SocketDataTypes.LobbyRowData {
    return {
        ...room,
        gameMapSetting: room.map,
        gameModeSetting: room.mode,
    }
}
